import React, {forwardRef, useEffect, useState} from 'react';

import {SnackbarContent} from 'notistack';
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import {Button, ButtonBase, LinearProgress, Typography} from "@mui/material";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import {useCountdown} from "@/hooks/useCountdown";
import FaIcon from "@/components/utilities/fa-icon";
import {setTab} from "@/redux/tabs.slice";
import {useAppDispatch} from "@/redux/hooks";
import {PATHS} from "@/utils/paths";
import Link from "next/link";


const snackRegistry = new Map<string, { update: (value: number) => void }>();

export function updateSnackMessage(snackId: string, countInCart: number) {
    const snack = snackRegistry.get(snackId);

    if (!snack) return false;

    snack.update(countInCart);
    return true;
}

const SnackMessage = forwardRef<
    HTMLDivElement,
    { snackId: string; productName: string; countInCart: number; mode?: "cart" | "preorder" }
>(
    function SnackMessageInner({ snackId, countInCart, productName, mode = "cart" }, ref) {
        const [timeLeft, {start}] = useCountdown(4000, 20);
        const dispatch = useAppDispatch();
        const theme = useTheme()
        const {width} = useWindowDimensions()
        const [displayCount, setDisplayCount] = useState(countInCart);

        useEffect(() => {
            start();
        }, []);

        useEffect(() => {
            snackRegistry.set(snackId, {
                update: (value: number) => {
                    setDisplayCount(value);
                }
            });

            return () => {
                snackRegistry.delete(snackId);
            };
        }, [snackId]);

        return (
            <SnackbarContent ref = {ref} >
                <Paper sx = {{bgcolor: theme.palette.background.paper}} >
                    {
                        width < theme.breakpoints.values['sm'] ?
                            <>
                                <Box
                                    display = 'flex'
                                    alignItems = 'center'
                                    justifyContent = 'space-between'
                                    width = '80vw'
                                    p = {2}
                                >
                                    <Typography fontWeight = {400} fontSize = {12} >
                                        {mode === "cart"
                                            ? `${displayCount} عدد از این کالا در سبد خرید شماست.`
                                            : `${displayCount} عدد از این کالا برای پیش‌خرید ثبت شد.`}
                                    </Typography >

                                    {mode === "cart" ? (
                                        <Button
                                            component = {Link}
                                            href = {PATHS.USER.CART}
                                            size = "small"
                                            color = "primary"
                                            sx = {{display: 'flex', flexDirection: 'column'}} >
                                            سبد خرید
                                        </Button >
                                    ) : (
                                        <Button size = "small" disabled
                                            startIcon = {<FaIcon faIcon = "fa-clock" />} >
                                            پیش‌خرید
                                        </Button >
                                    )}
                                </Box >

                                {
                                    timeLeft &&
                                    <LinearProgress sx = {{boxShadow: theme.customShadows.card}}
                                        color = 'secondary' variant = 'determinate'
                                        value = {(timeLeft / 4000) * 100} />
                                }
                            </>
                            :
                            <>
                                <Box
                                    display = 'flex'
                                    justifyContent = 'space-between'
                                    alignItems = 'center'
                                    width = {400}
                                    height = {100}
                                >
                                    <Box sx = {{p: 3, maxWidth: 300}} >
                                        {mode === "cart" ? (
                                            <>
                                                <Typography fontWeight={400} display="inline" color="primary">
                                                    {displayCount} عدد{" "}
                                                </Typography>

                                                <Typography fontWeight={400} display="inline">
                                                    {`«${productName}»`}
                                                </Typography>

                                                <Typography mt={1} color={theme.palette.grey[600]}>
                                                    در سبد خرید شماست.
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <Typography fontWeight = {400} display = "inline" color = "secondary" >
                                                    {displayCount} عدد{" "}
                                                </Typography >
                                                <Typography fontWeight = {500} display = "inline" >
                                                    {`«${productName}»`}
                                                </Typography >
                                                <Typography mt = {1} color = {theme.palette.grey[600]} >
                                                    درخواست پیش‌خرید شما با موفقیت ثبت شد.
                                                </Typography >
                                            </>
                                        )}

                                    </Box >
                                    <ButtonBase
                                        component = {Link}
                                        href = {PATHS.USER.CART}
                                        onClick = {() => {
                                            dispatch(
                                                setTab({
                                                    page: "cart",
                                                    value: mode === "cart" ? "cart" : "draftCart",
                                                })
                                            );
                                        }}
                                        sx = {{
                                            display: "flex",
                                            flexDirection: "column",
                                            height: 100,
                                            width: 100,
                                            opacity: 0.6,
                                            gap: .6
                                        }}
                                    >
                                        <FaIcon faIcon = {mode === "cart" ? "fa-shopping-cart" : "fa-clock"}
                                            fontSize = {22} color = {theme.palette.grey[900]} />
                                        {mode === "cart" ? "سبد خرید" : "پیش‌خرید"}
                                    </ButtonBase >
                                </Box >
                                {
                                    timeLeft ?
                                        <LinearProgress
                                            sx = {{boxShadow: theme.customShadows.card}}
                                            color = {mode === "cart" ?'primary':'secondary'}
                                            variant = 'determinate'
                                            value = {(timeLeft / 4000) * 100}
                                        /> : null
                                }
                            </>
                    }
                </Paper >

            </SnackbarContent >
        );
    });

export default SnackMessage;
